import React, { useState, useEffect } from 'react';
import { db } from './firebase'; 
import { 
  doc, updateDoc, deleteDoc, addDoc, collection, getDocs, query, where, writeBatch, orderBy, limit, getDoc, onSnapshot 
} from 'firebase/firestore'; 
import './App.css'; 

// --- IMPORTS ---
import RestCalculator from './components/RestCalculator';
import OvertimeRequest from './components/OvertimeRequest';
import DriverCalendar from './components/DriverCalendar';
import ComplianceDashboard from './components/ComplianceDashboard';
import AvailabilityMonitor from './components/AvailabilityMonitor';
import UserManagement from './components/UserManagement';
import ManagerAvailability from './components/ManagerAvailability'; 
import EmployeeDuty from './components/EmployeeDuty';
import HPlusS from './components/HPlusS';
import WeekendHours from './components/WeekendHours'; 
import Timesheet from './components/Timesheet'; 
import AdminTimesheet from './components/AdminTimesheet'; 
import PayPeriods from './components/PayPeriods'; 
import AdminTimesheetViewer from './components/AdminTimesheetViewer'; 
import {
  EMPLOYEE_HOME_URL,
  fetchPortalSession,
  getEmployeeLoginRedirectUrl,
  getMappedComplianceInitials,
  logoutPortalSession,
} from './auth/sso';

// --- MUI (Material Design 3-inspired UI) ---
import AppBar from '@mui/material/AppBar';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Fab from '@mui/material/Fab';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';

// --- GLOBAL HELPERS (Moved outside App to avoid scope issues) ---
const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();

const getPreviousThursday = (date = new Date()) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = (day + 7 - 4) % 7; 
  d.setDate(d.getDate() - diff);
  return d.toISOString().split('T')[0];
};

// ==========================================
// MAIN APP
// ==========================================
function App() {
  // --- STATE ---
  const [initials, setInitials] = useState('');
  const [pin, setPin] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userRole, setUserRole] = useState(''); 
  const [currentUserDisplayName, setCurrentUserDisplayName] = useState('');
  
  // Permissions State
  const [currentUserTimesheetRequired, setCurrentUserTimesheetRequired] = useState(false); 
  const [currentUserPayrollAccess, setCurrentUserPayrollAccess] = useState(false); 
  const [currentUserIsDrivingStaff, setCurrentUserIsDrivingStaff] = useState(false); 

  const [activeTab, setActiveTab] = useState('availability'); 
  const [driverSubTab, setDriverSubTab] = useState('overtime'); 
  
  // POPUP STATE (For Driver View)
  const [selectedDate, setSelectedDate] = useState(null); 
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const [managerViewMode, setManagerViewMode] = useState('rota'); 
  
  // --- ADMIN STATE RESTRUCTURE ---
  const [adminSubTab, setAdminSubTab] = useState('users'); 
  const [payrollSubTab, setPayrollSubTab] = useState('weekend'); 
  const [timesheetAdminSubTab, setTimesheetAdminSubTab] = useState('reporting'); 

  const [currentDate, setCurrentDate] = useState(new Date()); 
  
  // SINGLE SOURCE OF TRUTH (Yearly Data)
  const [yearlyAvailability, setYearlyAvailability] = useState({}); 
  const [originalYearlyAvailability, setOriginalYearlyAvailability] = useState({}); 
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false); 
  
  const [managerData, setManagerData] = useState([]); 
  const [saveMessage, setSaveMessage] = useState('');

  const [showChangePin, setShowChangePin] = useState(false);
  const [newUserPin, setNewUserPin] = useState('');
  const [authMethod, setAuthMethod] = useState(''); // 'pin' | 'sso'
  const [ssoChecking, setSsoChecking] = useState(true);
  const [ssoBusy, setSsoBusy] = useState(false);
  const [ssoError, setSsoError] = useState('');
  const [pendingMasterUser, setPendingMasterUser] = useState(null);
  const [loginError, setLoginError] = useState('');

  // --- ADMIN OVERTIME STATE ---
  const [allOtRequests, setAllOtRequests] = useState([]);
  const [isDenyPopupOpen, setIsDenyPopupOpen] = useState(false);
  const [denyReason, setDenyReason] = useState('');
  const [selectedOtRequestId, setSelectedOtRequestId] = useState(null);
  const [otPayrollFilterDate, setOtPayrollFilterDate] = useState(''); 
  const [adminOtSubTab, setAdminOtSubTab] = useState('requests');
  
  // --- PAYROLL QUERY STATE ---
  const [isQueryModalOpen, setIsQueryModalOpen] = useState(false);
  const [queryRequestId, setQueryRequestId] = useState(null);
  const [queryText, setQueryText] = useState('');
  const [isAnswerModalOpen, setIsAnswerModalOpen] = useState(false);
  const [answerRequestId, setAnswerRequestId] = useState(null);
  const [answerQueryId, setAnswerQueryId] = useState(null);
  const [answerText, setAnswerText] = useState('');
  const [answerChoice, setAnswerChoice] = useState('approve'); 

  const [usersList, setUsersList] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]); 
  const [logSelectedUser, setLogSelectedUser] = useState(''); 
  const canViewPinsPage = ['DJW', 'DOW'].includes(initials);

  // --- LOGGING ---
  const logAction = async (action, subject, details) => {
    try { await addDoc(collection(db, "audit_logs"), { timestamp: new Date(), actor: currentUserDisplayName || initials, actorInitials: initials, subject, action, details }); } catch (error) { console.error("Log failed", error); }
  };

  // --- FETCHERS ---
  const fetchUsers = async () => {
    const s = await getDocs(collection(db, "users"));
    const users = s.docs.map(d => d.data()).sort((a, b) =>
      String(a.displayName || a.initials || '').localeCompare(
        String(b.displayName || b.initials || ''),
        undefined,
        { sensitivity: 'base' }
      )
    );
    setUsersList(users);
  };
  const fetchLogs = async () => { const q = query(collection(db, "audit_logs"), orderBy("timestamp", "desc"), limit(100)); const s = await getDocs(q); setAuditLogs(s.docs.map(d => ({id: d.id, ...d.data()}))); };
  
  // MAIN FETCH: Gets the whole year of data
  const fetchYearlyAvailability = async () => {
    if (!initials) return;
    const year = currentDate.getFullYear();
    const q = query(collection(db, "availability"), where("initials", "==", initials), where("year", "==", year));
    const s = await getDocs(q);
    const data = {};
    s.forEach(doc => { 
        const r = doc.data(); 
        if (!data[r.month]) data[r.month] = {};
        data[r.month][r.day] = { status: r.status, timeNote: r.timeNote || "", managerDecision: r.managerDecision };
    });
    setYearlyAvailability(data);
    setOriginalYearlyAvailability(JSON.parse(JSON.stringify(data)));
    setSaveMessage('');
    setHasUnsavedChanges(false);
  };

  const fetchManagerData = async () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const nextMonth = (month + 1) % 12;
    const nextYear = month === 11 ? year + 1 : year;
    
    // Fetch current month data
    const q1 = query(collection(db, "availability"), where("year", "==", year), where("month", "==", month));
    const s1 = await getDocs(q1);
    const data = [];
    s1.forEach(doc => data.push({id: doc.id, ...doc.data()}));
    
    // Also fetch next month data (for cross-month weekends)
    const q2 = query(collection(db, "availability"), where("year", "==", nextYear), where("month", "==", nextMonth));
    const s2 = await getDocs(q2);
    s2.forEach(doc => data.push({id: doc.id, ...doc.data()}));
    
    setManagerData(data);
    setSaveMessage('');
  };

  // --- EFFECTS ---
  
  // 1. REAL-TIME OVERTIME LISTENER & NOTIFICATIONS
  useEffect(() => {
    // UPDATED: Added 'Manager' to allow standard managers to get OT data/notifications
    const canApproveOT = ['Admin', 'Super Admin', 'Payroll', 'Planner', 'Manager'].includes(userRole) || initials === 'DJW';
    
    if (canApproveOT && isLoggedIn) {
        if (Notification.permission !== 'granted' && Notification.permission !== 'denied') {
            Notification.requestPermission();
        }

        const q = collection(db, "overtime_requests");
        
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(d => ({id: d.id, ...d.data()}));
            data.sort((a, b) => (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0));
            setAllOtRequests(data);

            snapshot.docChanges().forEach((change) => {
                if (change.type === "added") {
                    const req = change.doc.data();
                    const now = new Date().getTime();
                    const reqTime = req.timestamp?.toMillis ? req.timestamp.toMillis() : (req.timestamp?.seconds * 1000 || 0);
                    
                    if (req.status === 'Pending Approval' && (now - reqTime < 30000)) {
                        if (Notification.permission === "granted") {
                            new Notification("New Overtime Request", {
                                body: `${req.displayName}: ${req.type}`,
                                icon: '/logo192.png'
                            });
                        }
                    }
                }
            });
        });

        return () => unsubscribe();
    }
  }, [userRole, initials, isLoggedIn]);


  useEffect(() => {
    // 2. Fetch Users - Added Manager
    const isElevated = ['Admin', 'Manager', 'Payroll', 'Planner', 'Super Admin'].includes(userRole) || initials === 'DJW' || initials === 'DOW';
    if (isElevated) fetchUsers(); 
    
    // 3. Fetch Logs
    const canViewLogs = ['Admin', 'Super Admin'].includes(userRole) || initials === 'DJW';
    if (canViewLogs && adminSubTab === 'logs') fetchLogs(); 

    // 4. Fetch Manager Rota Data
    if (isElevated && activeTab === 'manager') fetchManagerData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole, activeTab, currentDate, adminSubTab]);

  useEffect(() => {
    if (userRole === 'Payroll' && activeTab === 'admin' && adminSubTab !== 'payroll') {
        setAdminSubTab('payroll');
    }
    // Safety check for Managers to default to payroll/overtime if they click Admin
    if (userRole === 'Manager' && activeTab === 'admin' && adminSubTab !== 'payroll') {
        setAdminSubTab('payroll');
    }
  }, [userRole, activeTab, adminSubTab]);

  useEffect(() => { 
      if (isLoggedIn && !showChangePin) {
          if (currentUserIsDrivingStaff) fetchYearlyAvailability(); 
          setOtPayrollFilterDate(getPreviousThursday()); 
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate, isLoggedIn, showChangePin, currentUserIsDrivingStaff]);
   
  useEffect(() => {
    document.body.style.overflow = (isPopupOpen || isDenyPopupOpen) ? 'hidden' : 'auto';
  }, [isPopupOpen, isDenyPopupOpen]);

  useEffect(() => {
    setCurrentDate(new Date());
  }, [activeTab, managerViewMode]);

  // --- LOGIC ---
  const changeMonth = (offset) => {
    if (hasUnsavedChanges) {
        if (!window.confirm("You have unsaved changes. Discard them?")) return; 
    }
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + offset, 1)); 
    setHasUnsavedChanges(false); 
  };
  const handlePrevMonth = () => changeMonth(-1);
  const handleNextMonth = () => changeMonth(1);

  const handleAvailabilityUpdate = (month, day, field, value) => {
      setYearlyAvailability(prev => ({
          ...prev,
          [month]: {
              ...prev[month],
              [day]: { ...(prev[month]?.[day] || {}), [field]: value }
          }
      }));
      setHasUnsavedChanges(true);
  };

  const handleManagerDecision = (initials, day, decision) => { setManagerData(prev => prev.map(r => (r.initials === initials && r.day === day) ? {...r, managerDecision: decision} : r)); setSaveMessage(''); };
   
  const handleSaveManagerChanges = async () => {
    setSaveMessage('Saving...'); 
    const year = currentDate.getFullYear(); 
    const month = currentDate.getMonth(); 
    const batch = writeBatch(db); 
    let changesCount = 0;
    managerData.forEach(record => { 
        const docId = `${record.initials}_${year}_${month}_${record.day}`; 
        if (record.managerDecision !== undefined) { 
            batch.set(doc(db, "availability", docId), { 
                managerDecision: record.managerDecision, initials: record.initials, displayName: record.displayName,
                year: year, month: month, day: record.day, status: record.status || null 
            }, { merge: true }); 
            changesCount++; 
        } 
    });
    await batch.commit(); 
    setSaveMessage(`✅ ${changesCount} records updated!`); 
    setTimeout(() => { setSaveMessage(''); }, 3000);
  };
   
  const handleSaveChanges = async () => {
      setSaveMessage('Saving...'); const year = currentDate.getFullYear(); const batch = writeBatch(db); let changesDescription = []; 
      Object.keys(yearlyAvailability).forEach(m => {
          const monthData = yearlyAvailability[m];
          Object.keys(monthData).forEach(d => {
              const dayData = monthData[d];
              const oldData = originalYearlyAvailability[m]?.[d] || {};
              if (dayData.status !== oldData.status || dayData.timeNote !== oldData.timeNote) {
                  const docId = `${initials}_${year}_${m}_${d}`;
                  batch.set(doc(db, "availability", docId), { 
                      initials, displayName: currentUserDisplayName, year: Number(year), month: Number(m), day: Number(d), 
                      status: dayData.status || null, timeNote: dayData.timeNote || "", updatedAt: new Date() 
                  }, { merge: true });
                  changesDescription.push(`${monthNames[m]} ${d}: ${dayData.status}`);
              }
          });
      });
      await batch.commit(); 
      setOriginalYearlyAvailability(JSON.parse(JSON.stringify(yearlyAvailability)));
      setHasUnsavedChanges(false);
      setSaveMessage('✅ Saved!'); 
      setTimeout(() => { setSaveMessage(''); }, 3000);
  };

  // --- ADMIN ACTIONS ---
  const handleApproveOT = async (req) => {
      if(!window.confirm(`Approve OT for ${req.displayName}?`)) return;
      await updateDoc(doc(db, "overtime_requests", req.id), { status: "Approved", actionedBy: initials, actionedAt: new Date() });
      logAction("Approved OT", req.displayName, `${req.hours}h ${req.minutes}m on ${req.overtimeDate}`);
  };

  const handleOpenDeny = (id) => { setSelectedOtRequestId(id); setDenyReason(''); setIsDenyPopupOpen(true); };

  const handleDenySubmit = async () => {
      if(!denyReason) return alert("Please provide a reason.");
      await updateDoc(doc(db, "overtime_requests", selectedOtRequestId), { status: "Denied", managerNote: denyReason, actionedBy: initials, actionedAt: new Date() });
      setIsDenyPopupOpen(false); 
  };

  const handleAdminDeleteOT = async (id) => {
      if(!window.confirm("Permanently delete this record? This removes it from the Driver's view as well.")) return;
      await deleteDoc(doc(db, "overtime_requests", id)); 
  };

  const handleTogglePayroll = async (req) => { await updateDoc(doc(db, "overtime_requests", req.id), { payrollProcessed: !req.payrollProcessed }); };

  // --- PAYROLL QUERY HANDLERS ---
  const handleOpenQueryModal = (requestId) => {
    setQueryRequestId(requestId);
    setQueryText('');
    setIsQueryModalOpen(true);
  };

  const handleAddQuery = async () => {
    if (!queryText.trim()) return alert("Please enter a question.");
    if (!queryRequestId) return;
    
    try {
      const req = allOtRequests.find(r => r.id === queryRequestId);
      if (!req) return;
      
      const queries = req.queries || [];
      const newQuery = {
        id: Date.now().toString(),
        question: queryText,
        askedBy: initials,
        askedByName: currentUserDisplayName || initials,
        askedAt: new Date(),
        status: 'pending'
      };
      
      queries.push(newQuery);
      await updateDoc(doc(db, "overtime_requests", queryRequestId), { 
        queries,
        status: "Pending Manager Response"
      });
      setIsQueryModalOpen(false);
      setQueryText('');
      setSaveMessage("Query added. Sent to manager for response.");
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error("Error adding query:", error);
      alert("Failed to add query.");
    }
  };

  const handleOpenAnswerModal = (requestId, queryId) => {
    setAnswerRequestId(requestId);
    setAnswerQueryId(queryId);
    setAnswerText('');
    setAnswerChoice('approve');
    setIsAnswerModalOpen(true);
  };

  const handleSubmitAnswer = async () => {
    if (answerChoice === 'approve' && !answerText.trim()) return alert("Please enter an answer.");
    if (answerChoice === 'reject' && !answerText.trim()) return alert("Please provide a reason for rejecting.");
    if (!answerRequestId || !answerQueryId) return;
    
    try {
      const req = allOtRequests.find(r => r.id === answerRequestId);
      if (!req) return;
      
      const queries = req.queries || [];
      const queryIndex = queries.findIndex(q => q.id === answerQueryId);
      if (queryIndex === -1) return;
      
      queries[queryIndex].answer = answerText;
      queries[queryIndex].answeredBy = initials;
      queries[queryIndex].answeredByName = currentUserDisplayName || initials;
      queries[queryIndex].answeredAt = new Date();
      queries[queryIndex].status = answerChoice === 'approve' ? 'answered' : 'rejected';
      
      // If manager answers the query, set status back to Approved
      // If manager rejects the query, keep it as Pending Manager Response so Payroll can see the rejection
      const newStatus = answerChoice === 'approve' ? 'Approved' : 'Pending Manager Response';
      
      await updateDoc(doc(db, "overtime_requests", answerRequestId), { 
        queries,
        status: newStatus
      });
      logAction("Answered OT Query", req.displayName, `Query: ${queries[queryIndex].question} - Response: ${answerText}`);
      
      setIsAnswerModalOpen(false);
      setAnswerText('');
      setSaveMessage("Answer submitted.");
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error("Error submitting answer:", error);
      alert("Failed to submit answer.");
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleDeleteQuery = async (requestId, queryId) => {
    if (!window.confirm("Delete this query?")) return;
    try {
      const req = allOtRequests.find(r => r.id === requestId);
      if (!req) return;
      const queries = (req.queries || []).filter(q => q.id !== queryId);
      await updateDoc(doc(db, "overtime_requests", requestId), { queries });
    } catch (error) {
      console.error("Error deleting query:", error);
    }
  };

  // --- POPUP HANDLERS ---
  const handleDateClick = (month, day, currentData) => {
      const date = new Date(currentDate.getFullYear(), month, day);
      if(date.getDay() !== 6 && date.getDay() !== 0) return;
      setSelectedDate({ month, day, year: currentDate.getFullYear(), data: currentData || {} });
      setIsPopupOpen(true);
  };

  const handlePopupSave = (status) => {
      if(!selectedDate) return;
      handleAvailabilityUpdate(selectedDate.month, selectedDate.day, 'status', status);
      setSelectedDate(prev => ({...prev, data: {...prev.data, status: status}})); 
  };

  const handlePopupNoteChange = (e) => {
      const val = e.target.value;
      handleAvailabilityUpdate(selectedDate.month, selectedDate.day, 'timeNote', val);
      setSelectedDate(prev => ({...prev, data: {...prev.data, timeNote: val}}));
  };

  const handlePopupSaveAndClose = async () => {
      await handleSaveChanges();
      setIsPopupOpen(false);
  };

  // --- LOGGING ---
  const handleClearLogs = async () => {
    if (!window.confirm("ARE YOU SURE? This will permanently wipe all activity logs from the database.")) return;
    try {
        const q = query(collection(db, "audit_logs"));
        const snapshot = await getDocs(q);
        const batch = writeBatch(db);
        snapshot.docs.forEach((doc) => batch.delete(doc.ref));
        await batch.commit(); setAuditLogs([]); alert("All logs cleared.");
    } catch (e) { console.error("Error clearing logs: ", e); alert("Failed to clear logs."); }
  };
  
  // --- MASTER RESET ---
  const handleMasterReset = async () => {
    if (initials !== 'DJW') return; 
    if (!window.confirm("⚠️ DANGER: Wipe ALL Rota and Availability? Cannot be undone.")) return;
    if (!window.confirm("🛑 FINAL WARNING: Confirm wipe.")) return;
    setSaveMessage("Wiping database...");
    try {
        const q = query(collection(db, "availability"));
        const snapshot = await getDocs(q);
        const batch = writeBatch(db);
        snapshot.docs.forEach((doc) => batch.delete(doc.ref));
        await batch.commit();
        setYearlyAvailability({}); setManagerData([]); setOriginalYearlyAvailability({}); 
        setSaveMessage("✅ Database Wiped."); alert("Reset complete.");
    } catch (e) { console.error(e); alert("Error wiping data."); setSaveMessage("❌ Error"); }
  };

  // --- MIGRATION ---
  const handleMigrateDriverAccess = async () => {
    if (!window.confirm("This will enable 'Driving Staff' permission for ALL existing users. Continue?")) return;
    setSaveMessage("Migrating users...");
    try {
        const q = query(collection(db, "users"));
        const snapshot = await getDocs(q);
        const batch = writeBatch(db);
        let count = 0;
        snapshot.docs.forEach((doc) => {
             batch.update(doc.ref, { drivingStaff: true });
             count++;
        });
        await batch.commit();
        setSaveMessage(`✅ Migrated ${count} users.`);
        alert(`Success! ${count} users now have Driver Access.`);
        fetchUsers();
    } catch (e) {
        console.error(e);
        setSaveMessage("❌ Error");
        alert("Migration failed.");
    }
  };

  // --- LOGIN/LOGOUT ---
  const applyLocalUser = (u, loginInitials, method = 'pin') => {
      setInitials(loginInitials);
      setCurrentUserDisplayName(u.displayName || loginInitials);
      setUserRole(u.role || 'User');
      setCurrentUserTimesheetRequired(u.timesheetRequired || false);
      setCurrentUserIsDrivingStaff(u.drivingStaff || false);
      const hasImplicitAccess = ['Payroll', 'Admin', 'Super Admin'].includes(u.role);
      setCurrentUserPayrollAccess(u.payrollAccess || hasImplicitAccess);
      setAuthMethod(method);
      setPendingMasterUser(null);
      setSsoError('');
      setLoginError('');

      if (method === 'pin' && u.changePinRequired) {
          setShowChangePin(true);
          return;
      }

      setIsLoggedIn(true);
      if (u.role === 'Admin' || u.role === 'Super Admin') setActiveTab('admin');
      else if (u.role === 'Payroll') { setActiveTab('admin'); setAdminSubTab('payroll'); }
      else if (u.role === 'Manager' || u.role === 'Planner') setActiveTab('manager');
      else setActiveTab('staff');
  };

  const findLocalUserBySsoUid = async (masterUid) => {
      if (!masterUid) return null;
      const byUid = query(collection(db, 'users'), where('ssoUid', '==', masterUid));
      const snap = await getDocs(byUid);
      if (snap.empty) return null;
      const docSnap = snap.docs[0];
      return { id: docSnap.id, ...docSnap.data() };
  };

  const resolveSsoUser = async (masterUser, { allowLinkPrompt = true } = {}) => {
      const mappedInitials = getMappedComplianceInitials(masterUser);

      if (mappedInitials) {
          const snap = await getDoc(doc(db, 'users', mappedInitials));
          if (snap.exists()) {
              applyLocalUser(snap.data(), mappedInitials, 'sso');
              return { status: 'ok' };
          }
          return {
              status: 'missing_local',
              message: `Linked initials "${mappedInitials}" were not found in Weekend Availability.`,
          };
      }

      const linked = await findLocalUserBySsoUid(masterUser.uid);
      if (linked) {
          applyLocalUser(linked, linked.id || linked.initials, 'sso');
          return { status: 'ok' };
      }

      if (allowLinkPrompt) {
          setPendingMasterUser(masterUser);
          return {
              status: 'needs_link',
              message: 'Link your Country Lion account to your existing initials and PIN once.',
          };
      }

      return {
          status: 'needs_link',
          message: 'Your Country Lion account is not linked to a Weekend Availability profile yet.',
      };
  };

  const trySilentSso = async () => {
      setSsoChecking(true);
      setSsoError('');
      try {
          const session = await fetchPortalSession();
          if (!session.ok || !session.user) return;
          const result = await resolveSsoUser(session.user, { allowLinkPrompt: true });
          if (result.status === 'missing_local') setSsoError(result.message);
          if (result.status === 'needs_link') setSsoError(result.message);
      } catch (err) {
          console.warn('Silent SSO check failed', err);
      } finally {
          setSsoChecking(false);
      }
  };

  useEffect(() => {
      trySilentSso();
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSsoSignIn = async () => {
      setSsoBusy(true);
      setSsoError('');
      setLoginError('');
      try {
          const session = await fetchPortalSession();
          if (!session.ok || !session.user) {
              window.location.href = getEmployeeLoginRedirectUrl();
              return;
          }
          const result = await resolveSsoUser(session.user, { allowLinkPrompt: true });
          if (result.status !== 'ok') setSsoError(result.message);
      } catch (err) {
          console.error(err);
          setSsoError(err.message || 'SSO sign-in failed.');
      } finally {
          setSsoBusy(false);
      }
  };

  const handleLinkSsoAccount = async (e) => {
      e.preventDefault();
      if (!pendingMasterUser) return;
      setLoginError('');
      setSsoBusy(true);
      try {
          const linkInitials = initials.trim().toUpperCase();
          const snap = await getDoc(doc(db, 'users', linkInitials));
          if (!snap.exists() || snap.data().pin !== pin) {
              setLoginError('Invalid initials or PIN.');
              return;
          }
          await updateDoc(doc(db, 'users', linkInitials), {
              ssoUid: pendingMasterUser.uid,
              ssoEmail: pendingMasterUser.email || '',
              ssoLinkedAt: new Date().toISOString(),
          });
          applyLocalUser(snap.data(), linkInitials, 'sso');
      } catch (err) {
          console.error(err);
          setLoginError(err.message || 'Could not link account.');
      } finally {
          setSsoBusy(false);
      }
  };

  const handleLogin = async (e) => { 
      e.preventDefault();
      setLoginError('');
      if (initials === 'DJW' && pin === '1234') { 
          setUserRole('Super Admin'); 
          setIsLoggedIn(true); 
          setCurrentUserDisplayName("Super Admin"); 
          setCurrentUserTimesheetRequired(true); 
          setCurrentUserIsDrivingStaff(true); 
          setCurrentUserPayrollAccess(true);
          setAuthMethod('pin');
          setActiveTab('admin'); 
          return; 
      } 
      const snap = await getDoc(doc(db, "users", initials)); 
      if (snap.exists() && snap.data().pin === pin) { 
          applyLocalUser(snap.data(), initials, 'pin');
      } else {
          setLoginError('Invalid initials or PIN.');
      }
  };

  const handleLogout = async () => {
      if (authMethod === 'sso') {
          await logoutPortalSession();
      }
      setIsLoggedIn(false);
      setInitials('');
      setPin('');
      setAuthMethod('');
      setPendingMasterUser(null);
      setSsoError('');
      setLoginError('');
  };

  const handleChangePin = async (e) => { e.preventDefault(); await updateDoc(doc(db, "users", initials), {pin: newUserPin, changePinRequired: false}); window.location.reload(); };

  // --- BADGE CALCULATOR ---
  const getPendingOTCount = () => {
      if (!allOtRequests.length) return 0;
      
      return allOtRequests.filter(req => {
          if (req.status !== 'Pending Approval' && req.status !== 'Pending Manager Response') return false;
          
          const requester = usersList.find(u => u.initials === req.initials);
          const managers = requester?.assignedManager || [];
          
          // UPDATED LOGIC: Assigned Manager OR Super Admin
          return managers.includes(initials) || userRole === 'Super Admin';
      }).length;
  };

  // --- UI HELPERS ---
  const handleTopTabChange = (_e, value) => setActiveTab(value);

  // --- RENDER HELPERS ---
  const renderStaffView = () => (
      <Box className="animate-fade-in">
          <Paper variant="outlined" sx={{ borderRadius: 2, px: 1, mb: 3 }}>
              <Tabs
                value={driverSubTab}
                onChange={(_e, v) => setDriverSubTab(v)}
                variant="scrollable"
                allowScrollButtonsMobile
                sx={{ minHeight: 52 }}
              >
                  <Tab value="overtime" label="Overtime" />
                  {currentUserIsDrivingStaff && <Tab value="calendar" label="Weekend availability" />}
                  {currentUserTimesheetRequired && <Tab value="timesheet" label="Timesheet" />}
              </Tabs>
          </Paper>

          {driverSubTab === 'calendar' && currentUserIsDrivingStaff && (
            <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
              <DriverCalendar
                currentDate={currentDate}
                yearlyAvailability={yearlyAvailability}
                saveMessage={saveMessage}
                handleSaveChanges={handleSaveChanges}
                handleDateClick={handleDateClick}
              />
            </Paper>
          )}

          {driverSubTab === 'overtime' && (
            <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
              <OvertimeRequest db={db} initials={initials} displayName={currentUserDisplayName} setSaveMessage={setSaveMessage} />
            </Paper>
          )}

          {driverSubTab === 'timesheet' && currentUserTimesheetRequired && (
            <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
              <Timesheet db={db} initials={initials} />
            </Paper>
          )}
      </Box>
  );

  const renderManagerView = () => {
      const navButtons = (
        <Paper variant="outlined" sx={{ borderRadius: 2, px: 1, mb: 2 }}>
          <Tabs
            value={managerViewMode}
            onChange={(_e, v) => setManagerViewMode(v)}
            variant="scrollable"
            allowScrollButtonsMobile
            sx={{ minHeight: 52 }}
          >
            <Tab value="rota" label="Rota" />
            <Tab value="calculator" label="Rest" />
            <Tab value="h+s" label="H+S" />
            <Tab value="availability" label="Manage availability" />
            <Tab value="employeeDuty" label="Employee duty" />
            <Tab value="compliance" label="Compliance" />
            <Tab value="monitor" label="Monitor" />
          </Tabs>
        </Paper>
      );

      const monthControls = (
        <Paper variant="outlined" sx={{ borderRadius: 2, p: 1.25, px: 2, maxWidth: 520, mx: 'auto' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
            <Button variant="text" onClick={handlePrevMonth}>Prev</Button>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </Typography>
            <Button variant="text" onClick={handleNextMonth}>Next</Button>
          </Stack>
        </Paper>
      );

      const toggleHeader = (
        <Box sx={{ mb: 3 }}>
          {navButtons}
          {monthControls}
        </Box>
      );
      const toggleHeaderWithoutMonth = (
        <Box sx={{ mb: 3 }}>
          {navButtons}
        </Box>
      );

      if (managerViewMode === 'monitor') {
        return (
          <Box className="animate-fade-in">
            <Box sx={{ mb: 3 }}>{navButtons}</Box>
            <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
              <AvailabilityMonitor db={db} usersList={usersList} />
            </Paper>
          </Box>
        );
      }
      if (managerViewMode === 'compliance') {
        return (
          <Box className="animate-fade-in">
            {toggleHeader}
            <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
              <ComplianceDashboard db={db} usersList={usersList} currentDate={currentDate} />
            </Paper>
          </Box>
        );
      }
      if (managerViewMode === 'employeeDuty') {
        return (
          <Box className="animate-fade-in">
            {toggleHeader}
            <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
              <EmployeeDuty db={db} usersList={usersList} />
            </Paper>
          </Box>
        );
      }
      if (managerViewMode === 'calculator') {
        return (
          <Box className="animate-fade-in">
            {toggleHeader}
            <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
              <RestCalculator
                db={db}
                usersList={usersList}
                currentDate={currentDate}
                managerData={managerData}
                fetchManagerData={fetchManagerData}
                setSaveMessage={setSaveMessage}
              />
            </Paper>
          </Box>
        );
      }
      if (managerViewMode === 'h+s') {
        return (
          <Box className="animate-fade-in">
            {toggleHeaderWithoutMonth}
            <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
              <HPlusS
                db={db}
                usersList={usersList}
                currentUserInitials={initials}
                currentUserDisplayName={currentUserDisplayName}
                currentUserRole={userRole}
              />
            </Paper>
          </Box>
        );
      }
      if (managerViewMode === 'availability') {
        return (
          <Box className="animate-fade-in">
            {toggleHeader}
            <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
              <ManagerAvailability db={db} usersList={usersList} currentDate={currentDate} logAction={logAction} initials={initials} />
            </Paper>
          </Box>
        );
      }

      // ROTA VIEW RENDER
      const days = getDaysInMonth(currentDate.getFullYear(), currentDate.getMonth());
      const cards = [];
      for(let d=1; d<=days; d++) {
          const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), d);
          if(date.getDay()!==6 && date.getDay()!==0) continue;
          let drivers = managerData.filter(r => r.day === d && (r.status === 'Available' || r.status === 'Want to Work' || r.managerDecision === 'Working'));
          drivers.sort((a, b) => { const scoreA = a.status === 'Want to Work' ? 2 : (a.status === 'Available' ? 1 : 0); const scoreB = b.status === 'Want to Work' ? 2 : (b.status === 'Available' ? 1 : 0); return scoreB - scoreA; });
          cards.push(
            <Paper key={d} variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {date.getDay()===6?'Sat':'Sun'} {d}
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
              <Box sx={{ maxHeight: 320, overflowY: 'auto', pr: 0.5 }}>
                {drivers.length === 0 ? (
                  <Typography variant="body2" sx={{ opacity: 0.7 }}>No drivers available.</Typography>
                ) : (
                  drivers.map(r => {
                    const isConflict = r.managerDecision === 'Working' && r.status === 'Unavailable';
                    return (
                      <Box
                        key={r.initials}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1.5,
                          py: 1,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          bgcolor: isConflict ? 'rgba(179, 38, 30, 0.10)' : 'transparent',
                          px: 1,
                          borderRadius: 2,
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: r.managerDecision==='Working' ? 800 : 500,
                              color: r.managerDecision==='Not Legal' ? 'error.main' : 'text.primary',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {r.displayName} ({r.jobRole || 'Driver'})
                          </Typography>
                          {r.timeNote && (
                            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                              Notes: {r.timeNote}
                            </Typography>
                          )}
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                            <Chip size="small" label={r.status || '—'} variant="outlined" />
                            {isConflict && <Chip size="small" color="error" label="Unavailable" />}
                          </Stack>
                        </Box>
                        <TextField
                          select
                          value={r.managerDecision || ""}
                          onChange={(e) => handleManagerDecision(r.initials, d, e.target.value === "" ? null : e.target.value)}
                          SelectProps={{ native: true }}
                          size="small"
                          sx={{ width: 170 }}
                        >
                          <option value="">Unassigned</option>
                          <option value="Working">Working</option>
                          <option value="Not Legal">Not Legal</option>
                        </TextField>
                      </Box>
                    );
                  })
                )}
              </Box>
            </Paper>
          );
      }

      if (managerViewMode === 'rota') {
        return (
          <Box className="animate-fade-in">
            {toggleHeader}
            <div className="calendar-grid">{cards}</div>
            <Tooltip title="Save rota changes">
              <Fab
                color="primary"
                onClick={handleSaveManagerChanges}
                sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 50 }}
                variant="extended"
              >
                <SaveRoundedIcon sx={{ mr: 1 }} /> Save
              </Fab>
            </Tooltip>
          </Box>
        );
      }
  };

  // 3. ADMIN VIEW & LOGIN
  if (showChangePin) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          px: 2,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          aria-hidden
          sx={{
            pointerEvents: 'none',
            position: 'fixed',
            inset: 0,
            zIndex: 0,
            background:
              'radial-gradient(ellipse at top, #0a0a0f 0%, #050506 50%, #020203 100%)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -160,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 1400,
              height: 900,
              borderRadius: '50%',
              background: 'rgba(94,106,210,0.25)',
              filter: 'blur(150px)',
            },
          }}
        />
        <Paper variant="outlined" sx={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 460, p: 3.5, borderRadius: 3 }}>
          <Typography variant="h5" sx={{ mb: 0.5 }}>Change PIN required</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Please set a new PIN to continue.
          </Typography>
          <Box component="form" onSubmit={handleChangePin} sx={{ display: 'grid', gap: 2 }}>
            <TextField
              type="password"
              label="New PIN"
              onChange={e=>setNewUserPin(e.target.value)}
              autoFocus
            />
            <Button type="submit">Update PIN</Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  if (!isLoggedIn) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          px: 2,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          aria-hidden
          sx={{
            pointerEvents: 'none',
            position: 'fixed',
            inset: 0,
            zIndex: 0,
            background:
              'radial-gradient(ellipse at top, #0a0a0f 0%, #050506 50%, #020203 100%)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -160,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 1400,
              height: 900,
              borderRadius: '50%',
              background: 'rgba(94,106,210,0.25)',
              filter: 'blur(150px)',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              top: '30%',
              left: -160,
              width: 600,
              height: 800,
              borderRadius: '50%',
              background: 'rgba(124,77,255,0.15)',
              filter: 'blur(120px)',
            },
          }}
        />
        <Paper variant="outlined" sx={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 460, p: 3.5, borderRadius: 3 }}>
          <Typography
            variant="overline"
            sx={{ color: 'primary.main', letterSpacing: '0.18em', fontFamily: 'monospace' }}
          >
            Country Lion
          </Typography>
          <Typography variant="h5" sx={{ mb: 0.5, mt: 0.5 }}>Weekend Availability</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Sign in with Country Lion SSO, or continue with initials and PIN.
          </Typography>

          {ssoChecking ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Checking Country Lion session…
            </Typography>
          ) : (
            <Stack spacing={1.5} sx={{ mb: 2.5 }}>
              <Button
                variant="contained"
                onClick={handleSsoSignIn}
                disabled={ssoBusy}
              >
                {ssoBusy ? 'Signing in…' : 'Sign in with Country Lion'}
              </Button>
              {(ssoError || pendingMasterUser) && (
                <Typography variant="body2" color="warning.main">
                  {ssoError}
                </Typography>
              )}
            </Stack>
          )}

          <Divider sx={{ my: 2 }}>
            <Typography variant="caption" color="text.secondary">or PIN login</Typography>
          </Divider>

          {pendingMasterUser ? (
            <Box component="form" onSubmit={handleLinkSsoAccount} sx={{ display: 'grid', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Signed in as {pendingMasterUser.fullName || pendingMasterUser.email}. Enter your
                existing initials and PIN once to link this account.
              </Typography>
              <TextField
                label="Initials"
                value={initials}
                placeholder="e.g. JD"
                onChange={e=>setInitials(e.target.value.toUpperCase())}
                inputProps={{ maxLength: 6 }}
              />
              <TextField
                type="password"
                label="PIN"
                value={pin}
                onChange={e=>setPin(e.target.value)}
              />
              {loginError && (
                <Typography variant="body2" color="error">{loginError}</Typography>
              )}
              <Button type="submit" disabled={ssoBusy}>
                {ssoBusy ? 'Linking…' : 'Link and continue'}
              </Button>
              <Button
                variant="text"
                onClick={() => {
                  setPendingMasterUser(null);
                  setSsoError('');
                  setLoginError('');
                }}
              >
                Cancel linking
              </Button>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleLogin} sx={{ display: 'grid', gap: 2 }}>
              <TextField
                label="Initials"
                placeholder="e.g. JD"
                onChange={e=>setInitials(e.target.value.toUpperCase())}
                inputProps={{ maxLength: 6 }}
              />
              <TextField type="password" label="PIN" onChange={e=>setPin(e.target.value)} />
              {loginError && (
                <Typography variant="body2" color="error">{loginError}</Typography>
              )}
              <Button type="submit" variant="outlined">Sign in with PIN</Button>
            </Box>
          )}
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', position: 'relative' }}>
      <AppBar position="sticky" elevation={0} color="transparent">
        <Toolbar sx={{ gap: 2 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="overline" sx={{ color: 'primary.main', letterSpacing: '0.14em', lineHeight: 1 }}>
              Country Lion
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.15 }}>
              {currentUserDisplayName || 'Weekend Availability'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {userRole || 'Staff'}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<HomeRoundedIcon />}
            href={EMPLOYEE_HOME_URL}
          >
            Home
          </Button>
          <Button
            variant="outlined"
            startIcon={<LogoutRoundedIcon />}
            onClick={handleLogout}
          >
            Logout
          </Button>
        </Toolbar>
        <Divider />
        <Box sx={{ px: 2 }}>
          <Tabs
            value={activeTab}
            onChange={handleTopTabChange}
            variant="scrollable"
            allowScrollButtonsMobile
          >
            <Tab value="staff" label="Staff" />
            {['Manager', 'Planner', 'Admin', 'Super Admin'].includes(userRole) && (
              <Tab value="manager" label="Manager" />
            )}
            {userRole === 'Manager' && <Tab value="compliance" label="Compliance" />}
            {['Admin', 'Super Admin', 'Payroll', 'Planner', 'Manager'].includes(userRole) && (
              <Tab
                value="admin"
                label={
                  <Badge
                    color="error"
                    badgeContent={getPendingOTCount()}
                    invisible={getPendingOTCount() === 0}
                  >
                    Admin
                  </Badge>
                }
              />
            )}
          </Tabs>
        </Box>
      </AppBar>

      <Container className="app-content" maxWidth="lg" sx={{ pt: 3 }}>
          {activeTab === 'staff' && renderStaffView()}
          {activeTab === 'manager' && renderManagerView()}
          {activeTab === 'compliance' && (
            <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
              <ComplianceDashboard db={db} usersList={usersList} currentDate={currentDate} />
            </Paper>
          )}
          {activeTab === 'admin' && (
              <Box className="animate-fade-in">
                  <div style={{display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '40px'}}>
                      {/* USER & LOGS: Admin & Super Admin Only */}
                      {['Admin', 'Super Admin'].includes(userRole) && (
                          <>
                            <Button variant={adminSubTab==='users' ? 'contained' : 'outlined'} onClick={()=>setAdminSubTab('users')}>User management</Button>
                            <Button variant={adminSubTab==='logs' ? 'contained' : 'outlined'} onClick={()=>setAdminSubTab('logs')}>Activity logs</Button>
                          </>
                      )}
                      
                      {/* PAYROLL: Payroll, Planner, Admin, Super Admin, Manager (OT Only) */}
                      {['Payroll', 'Planner', 'Admin', 'Super Admin', 'Manager'].includes(userRole) && (
                          <Button variant={adminSubTab==='payroll' ? 'contained' : 'outlined'} onClick={()=>setAdminSubTab('payroll')}>
                            <Badge
                              color="error"
                              badgeContent={getPendingOTCount()}
                              invisible={getPendingOTCount() === 0}
                            >
                              Payroll
                            </Badge>
                          </Button>
                      )}
                      
                      {/* SYSTEM: Admin & Super Admin Only */}
                      {['Admin', 'Super Admin'].includes(userRole) && (
                          <Button variant={adminSubTab==='system' ? 'contained' : 'outlined'} onClick={()=>setAdminSubTab('system')}>System</Button>
                      )}

                      {canViewPinsPage && (
                          <Button variant={adminSubTab==='userPins' ? 'contained' : 'outlined'} onClick={()=>setAdminSubTab('userPins')}>User PINs</Button>
                      )}
                  </div>

                  {adminSubTab === 'users' && <UserManagement db={db} usersList={usersList} fetchUsers={fetchUsers} />}

                  {adminSubTab === 'userPins' && (
                      <Paper variant="outlined" sx={{maxWidth:'900px', mx:'auto', textAlign:'left', p:3, borderRadius: 2}}>
                          <Typography variant="h6" sx={{mb: 0.5}}>User PINs</Typography>
                          <Typography variant="body2" sx={{opacity: 0.75, mb: 2}}>Visible only to DJW and DOW.</Typography>
                          <div style={{overflowX:'auto'}}>
                              <table style={{width:'100%', borderCollapse:'collapse'}}>
                                  <thead>
                                      <tr style={{background:'var(--light)'}}>
                                          <th style={{textAlign:'left', padding:'12px 10px', borderBottom:'1px solid #e5e7eb'}}>Name</th>
                                          <th style={{textAlign:'left', padding:'12px 10px', borderBottom:'1px solid #e5e7eb'}}>Initials</th>
                                          <th style={{textAlign:'left', padding:'12px 10px', borderBottom:'1px solid #e5e7eb'}}>PIN</th>
                                      </tr>
                                  </thead>
                                  <tbody>
                                      {[...usersList].sort((a,b)=> (a.displayName || a.initials).localeCompare(b.displayName || b.initials)).map(user => (
                                          <tr key={user.initials} style={{borderBottom:'1px solid #e5e7eb'}}>
                                              <td style={{padding:'12px 10px'}}>{user.displayName || 'Unknown'}</td>
                                              <td style={{padding:'12px 10px'}}>{user.initials}</td>
                                              <td style={{padding:'12px 10px'}}>{user.pin || '—'}</td>
                                          </tr>
                                      ))}
                                  </tbody>
                              </table>
                          </div>
                      </Paper>
                  )}
                  
                  {adminSubTab === 'payroll' && (
                      <Paper variant="outlined" sx={{maxWidth:'1000px', mx:'auto', textAlign:'left', p:3, borderRadius: 2}}>
                          <div style={{display:'flex', gap:'10px', marginBottom:'20px', borderBottom:'1px solid #e5e7eb', paddingBottom:'15px'}}>
                              {/* Weekend Hours: Hidden from Manager */}
                              {userRole !== 'Manager' && (
                                  <Button variant={payrollSubTab==='weekend' ? 'contained' : 'outlined'} onClick={()=>setPayrollSubTab('weekend')}>Weekend hours</Button>
                              )}
                              
                              <Button variant={payrollSubTab==='overtime' ? 'contained' : 'outlined'} onClick={()=>setPayrollSubTab('overtime')}>
                                <Badge color="error" badgeContent={getPendingOTCount()} invisible={getPendingOTCount() === 0}>
                                  Overtime
                                </Badge>
                              </Button>
                              
                              {/* Timesheets: Payroll, Admin, Super Admin OR user with explicit payroll access */}
                              {(['Payroll', 'Admin', 'Super Admin'].includes(userRole) || currentUserPayrollAccess) && (
                                  <>
                                      <Button variant={payrollSubTab==='timesheets' ? 'contained' : 'outlined'} onClick={()=>setPayrollSubTab('timesheets')}>Timesheets</Button>
                                      <Button variant={payrollSubTab==='view-timesheets' ? 'contained' : 'outlined'} onClick={()=>setPayrollSubTab('view-timesheets')}>View timesheets</Button>
                                  </>
                              )}
                          </div>
                          
                          {payrollSubTab === 'weekend' && userRole !== 'Manager' && <WeekendHours db={db} usersList={usersList} initials={initials} setSaveMessage={setSaveMessage} />}
                          
                          {payrollSubTab === 'overtime' && (
                              <div>
                                  <div style={{display:'flex', gap:'10px', marginBottom:'20px', borderBottom:'1px solid #e5e7eb', paddingBottom:'15px'}}>
                                      <Button size="small" variant={adminOtSubTab==='requests' ? 'contained' : 'outlined'} onClick={()=>setAdminOtSubTab('requests')}>
                                        <Badge color="error" badgeContent={getPendingOTCount()} invisible={getPendingOTCount() === 0}>
                                          Requests
                                        </Badge>
                                      </Button>
                                      <Button size="small" variant={adminOtSubTab==='approved' ? 'contained' : 'outlined'} onClick={()=>setAdminOtSubTab('approved')}>Approved</Button>
                                      <Button size="small" variant={adminOtSubTab==='history' ? 'contained' : 'outlined'} onClick={()=>setAdminOtSubTab('history')}>History</Button>
                                  </div>
                                  
                                  {adminOtSubTab === 'requests' && (
                                      <div style={{display:'flex', flexDirection:'column', gap:'10px'}}>
                                          {allOtRequests.filter(r=>r.status==='Pending Approval' || r.status==='Pending Manager Response').length === 0 ? <p className="text-sm">No pending requests.</p> : 
                                              allOtRequests.filter(r=>r.status==='Pending Approval' || r.status==='Pending Manager Response').map(r => {
                                                  const requester = usersList.find(u => u.initials === r.initials);
                                                  const managers = requester?.assignedManager || [];
                                                  
                                                  // UPDATED: Strict Line Manager Check
                                                  // You can only approve if you are in the list of assigned managers OR Super Admin
                                                  const canApprove = managers.includes(initials) || userRole === 'Super Admin';
                                                  
                                                  return (
                                                      <div key={r.id} style={{background:'var(--light)', padding:'15px', borderRadius:'12px', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                                                          <div>
                                                              <div style={{fontWeight:'bold', fontSize:'14px'}}>{r.displayName} ({r.initials})</div>
                                                              <div style={{fontSize:'13px'}}>W/C: {r.weekCommencing} | <strong>Worked: {r.overtimeDate}</strong></div>
                                                              <div style={{fontSize:'12px', fontWeight:'600', color:'#2E6CF6', marginTop:'4px'}}>{r.type}</div>
                                                              <div style={{fontSize:'13px', marginTop:'4px'}}><strong>{r.details}</strong></div>
                                                              <div style={{fontSize:'13px', marginTop:'2px'}}>Total: {r.hours}h {r.minutes}m <span style={{fontSize:'11px', color:'var(--text-muted)'}}>(Unsoc: {r.unsocHours}h {r.unsocMinutes}m)</span></div>
                                                              <div style={{fontSize:'12px', color:'var(--text-muted)', marginTop:'4px'}}>"{r.reason}"</div>
                                                              <div style={{fontSize:'11px', color:'#2E6CF6', marginTop:'4px'}}>Assigned Managers: {managers.join(', ') || 'None'}</div>
                                                          </div>
                                                          {canApprove ? (
                                                            <div style={{display:'flex', flexDirection:'column', gap:'8px'}}>
                                                              <div className="flex-gap">
                                                                <Button color="success" onClick={()=>handleApproveOT(r)}>Approve</Button>
                                                                <Button color="error" variant="outlined" onClick={()=>handleOpenDeny(r.id)}>Deny</Button>
                                                              </div>
                                                              {r.queries && r.queries.filter(q=>q.status==='pending').length > 0 && (
                                                                <Paper variant="outlined" sx={{ p: 1, borderRadius: 2, bgcolor: 'rgba(184,92,0,0.08)', borderColor: 'rgba(184,92,0,0.30)' }}>
                                                                  <Typography variant="caption" sx={{ fontWeight: 800, display: 'block', mb: 0.5 }}>
                                                                    Pending queries
                                                                  </Typography>
                                                                  {r.queries.filter(q=>q.status==='pending').map((q,i)=>(
                                                                    <Typography key={i} variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                                                                      <Button variant="text" size="small" onClick={()=>handleOpenAnswerModal(r.id, q.id)} sx={{ px: 0, minWidth: 0 }}>
                                                                        Answer
                                                                      </Button>
                                                                      {' '}“{q.question}”
                                                                    </Typography>
                                                                  ))}
                                                                </Paper>
                                                              )}
                                                            </div>
                                                          ) : (
                                                            <Typography variant="caption" sx={{ opacity: 0.75, fontStyle: 'italic' }}>
                                                              Awaiting: {managers.length > 0 ? managers.join(', ') : 'Manager assignment'}
                                                            </Typography>
                                                          )}
                                                      </div>
                                                  )
                                              })
                                          }
                                      </div>
                                  )}
                                  
                                  {adminOtSubTab === 'approved' && (
                                      <div>
                                          <div style={{marginBottom:'20px', background:'var(--light)', padding:'15px', borderRadius:'12px'}}>
                                              <label style={{fontWeight:'bold', fontSize:'12px'}}>Filter by Pay Week (Thu-Wed):</label>
                                              <div style={{display:'flex', gap:'10px', alignItems:'center', marginTop:'5px'}}>
                                                  <input type="date" value={otPayrollFilterDate} onChange={e => { if(e.target.value) { setOtPayrollFilterDate(getPreviousThursday(e.target.value)); } else { setOtPayrollFilterDate(''); } }} />
                                                  <span style={{fontSize:'12px', color:'var(--text-muted)'}}>(Start: Thursday {otPayrollFilterDate || '...'})</span>
                                              </div>
                                          </div>
                                          {/* Pending Warning Logic */}
                                          {(() => {
                                              if (!otPayrollFilterDate) return null;
                                              const start = new Date(otPayrollFilterDate); const end = new Date(start); end.setDate(end.getDate() + 6);
                                              const pendingCount = allOtRequests.filter(r => { if (r.status !== 'Pending Approval') return false; const d = new Date(r.overtimeDate); return d >= start && d <= end; }).length;
                                              if (pendingCount > 0) return (<div style={{background:'rgba(245,158,11,0.15)', border:'1px solid rgba(245,158,11,0.45)', color:'#fbbf24', padding:'10px', borderRadius:'8px', marginBottom:'15px', fontSize:'13px', fontWeight:'bold'}}>⚠️ Warning: There are {pendingCount} pending requests for this pay week.</div>);
                                              return null;
                                          })()}
                                          <div style={{display:'flex', flexDirection:'column', gap:'10px'}}>
                                              {allOtRequests.filter(r => { if (r.status !== 'Approved') return false; if (!otPayrollFilterDate) return true; const filterStart = new Date(otPayrollFilterDate); const filterEnd = new Date(filterStart); filterEnd.setDate(filterEnd.getDate() + 6); const itemDate = new Date(r.overtimeDate); return itemDate >= filterStart && itemDate <= filterEnd; }).map(r => {
                                                  const hasQueries = r.queries && r.queries.length > 0;
                                                  return (
                                                    <div key={r.id} style={{background: r.payrollProcessed ? 'rgba(27,143,90,0.15)' : 'rgba(94,106,210,0.12)', border: r.payrollProcessed ? '1px solid rgba(132,204,22,0.45)' : '1px solid rgba(94,106,210,0.35)', padding:'15px', borderRadius:'8px'}}>
                                                      <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start'}}>
                                                          <div style={{flex:1}}>
                                                              <div style={{fontWeight:'bold', fontSize:'14px'}}>{r.displayName} ({r.initials})</div>
                                                              <div style={{fontSize:'13px'}}>W/C: {r.weekCommencing} | <strong>Worked: {r.overtimeDate}</strong></div>
                                                              <div style={{fontSize:'12px', fontWeight:'600', color:'#a5b0f0', marginTop:'4px'}}>{r.type}</div>
                                                              <div style={{fontSize:'13px', marginTop:'2px'}}><strong>{r.details}</strong></div>
                                                              <div style={{fontSize:'13px', marginTop:'4px'}}>Total: {r.hours}h {r.minutes}m <span style={{fontSize:'11px', color:'var(--text-muted)'}}>(Unsoc: {r.unsocHours}h {r.unsocMinutes}m)</span></div>
                                                              <div style={{fontSize:'10px', color:'var(--text-muted)', marginTop:'4px'}}>Approved by {r.actionedBy}</div>
                                                          </div>
                                                          <div style={{textAlign:'right', whiteSpace:'nowrap', marginLeft:'10px'}}>
                                                              <Button
                                                                size="small"
                                                                color={r.payrollProcessed ? 'success' : 'inherit'}
                                                                onClick={() => handleTogglePayroll(r)}
                                                                sx={{ width: '100%', mb: 1 }}
                                                              >
                                                                {r.payrollProcessed ? 'Processed' : 'Mark processed'}
                                                              </Button>
                                                              <Button
                                                                size="small"
                                                                variant="outlined"
                                                                onClick={() => handleOpenQueryModal(r.id)}
                                                                sx={{ width: '100%' }}
                                                              >
                                                                Ask query
                                                              </Button>
                                                          </div>
                                                      </div>
                                                      {hasQueries && (
                                                        <div style={{marginTop:'12px', paddingTop:'12px', borderTop:'1px solid var(--border)'}}>
                                                          <div style={{fontWeight:'bold', fontSize:'12px', marginBottom:'8px', color:'#a5b0f0'}}>Queries:</div>
                                                          {r.queries.map(q => (
                                                            <div key={q.id} style={{background:'var(--light)', padding:'10px', borderRadius:'6px', marginBottom:'8px', border:'1px solid var(--border)', fontSize:'12px'}}>
                                                              <div style={{display:'flex', justifyContent:'space-between', alignItems:'start', marginBottom:'4px'}}>
                                                              <Chip
                                                                size="small"
                                                                label={q.status.toUpperCase()}
                                                                color={q.status==='pending' ? 'warning' : (q.status==='answered' ? 'success' : 'error')}
                                                                variant="outlined"
                                                              />
                                                              <Typography variant="caption" sx={{ opacity: 0.75 }}>
                                                                {q.askedByName} · {new Date(q.askedAt?.seconds ? q.askedAt.seconds*1000 : q.askedAt).toLocaleDateString()}
                                                              </Typography>
                                                              </div>
                                                              <div style={{margin:'6px 0', fontStyle:'italic', color:'var(--text-muted)'}}>Q: {q.question}</div>
                                                              {q.answer && <div style={{margin:'6px 0', padding:'6px', background:'rgba(255,255,255,0.04)', borderRadius:'4px', color:'var(--text-main)'}}>A: {q.answer}</div>}
                                                              {q.answeredBy && (
                                                                <Typography variant="caption" sx={{ opacity: 0.75, mt: 0.5, display: 'block' }}>
                                                                  Answered by {q.answeredByName}
                                                                </Typography>
                                                              )}
                                                            </div>
                                                          ))}
                                                        </div>
                                                      )}
                                                    </div>
                                                  );
                                              })}
                                          </div>
                                      </div>
                                  )}
                                  {adminOtSubTab === 'history' && (
                                      <div style={{display:'flex', flexDirection:'column', gap:'10px'}}>
                                          {allOtRequests.filter(r => r.status === 'Approved' || r.status === 'Denied').map(r => (
                                              <div key={r.id} style={{background: r.status==='Denied'?'rgba(179,38,30,0.15)':'rgba(27,143,90,0.15)', border:'1px solid var(--border)', padding:'15px', borderRadius:'8px', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                                                  <div>
                                                      <div style={{fontWeight:'bold', fontSize:'14px'}}>{r.displayName} ({r.initials})</div>
                                                      <div style={{fontSize:'13px'}}>W/C: {r.weekCommencing} | <strong>Worked: {r.overtimeDate}</strong></div>
                                                      <div style={{fontSize:'12px', fontWeight:'600', color:'#2E6CF6', marginTop:'4px'}}>{r.type}</div>
                                                      <div style={{fontSize:'13px', marginTop:'4px'}}><strong>{r.details}</strong></div>
                                                      <div style={{fontSize:'13px', marginTop:'2px'}}>Total: {r.hours}h {r.minutes}m <span style={{fontSize:'11px', color:'var(--text-muted)'}}>(Unsoc: {r.unsocHours}h {r.unsocMinutes}m)</span></div>
                                                      {r.managerNote && (
                                                        <Typography variant="body2" sx={{ fontWeight: 800, color: 'error.main', mt: 0.5 }}>
                                                          Note: {r.managerNote}
                                                        </Typography>
                                                      )}
                                                  </div>
                                                  <div style={{textAlign:'right', display:'flex', flexDirection:'column', alignItems:'flex-end'}}>
                                                      <Chip size="small" color={r.status==='Approved' ? 'success' : 'error'} label={r.status} />
                                                      <Button
                                                        size="small"
                                                        color="error"
                                                        variant="outlined"
                                                        onClick={(e) => { e.stopPropagation(); handleAdminDeleteOT(r.id); }}
                                                        sx={{ mt: 1 }}
                                                      >
                                                        Delete
                                                      </Button>
                                                  </div>
                                              </div>
                                          ))}
                                      </div>
                                  )}
                              </div>
                          )}

                          {payrollSubTab === 'timesheets' && (['Payroll', 'Admin', 'Super Admin'].includes(userRole) || currentUserPayrollAccess) && (
                              <div>
                                  <div style={{display:'flex', gap:'10px', marginBottom:'20px', borderBottom:'1px solid #e5e7eb', paddingBottom:'15px'}}>
                                      <Button size="small" variant={timesheetAdminSubTab==='reporting' ? 'contained' : 'outlined'} onClick={()=>setTimesheetAdminSubTab('reporting')}>Reporting</Button>
                                      <Button size="small" variant={timesheetAdminSubTab==='payperiods' ? 'contained' : 'outlined'} onClick={()=>setTimesheetAdminSubTab('payperiods')}>Pay periods</Button>
                                  </div>
                                  
                                  {timesheetAdminSubTab === 'reporting' && <AdminTimesheet db={db} usersList={usersList} />}
                                  {timesheetAdminSubTab === 'payperiods' && <PayPeriods db={db} />}
                              </div>
                          )}

                          {payrollSubTab === 'view-timesheets' && (['Payroll', 'Super Admin'].includes(userRole) || currentUserPayrollAccess) && (
                              <AdminTimesheetViewer db={db} usersList={usersList} />
                          )}
                      </Paper>
                  )}

                  {adminSubTab === 'system' && (
                      <Paper variant="outlined" sx={{maxWidth:'600px', mx:'auto', textAlign:'center', borderRadius: 2, p:3, borderColor:'rgba(179,38,30,0.25)'}}>
                          <Typography variant="h6" sx={{color:'error.main', mb: 2}}>System administration</Typography>
                          <div style={{borderTop:'1px solid var(--border)', padding:'20px 0'}}>
                              <h4 style={{marginBottom:'10px'}}>Activity Logs</h4>
                              <p className="text-sm" style={{marginBottom:'15px'}}>Clear history of changes.</p>
                              <Button color="error" onClick={handleClearLogs}>Clear all logs</Button>
                          </div>
                          {/* MIGRATION BUTTON */}
                          <div style={{borderTop:'1px solid var(--border)', padding:'20px 0'}}>
                              <h4 style={{marginBottom:'10px'}}>Data Migrations</h4>
                              <Button color="warning" variant="outlined" onClick={handleMigrateDriverAccess}>
                                Enable driver access for all
                              </Button>
                          </div>
                          {initials === 'DJW' && (
                              <div style={{borderTop:'1px solid rgba(179,38,30,0.45)', padding:'20px 0', background:'rgba(179,38,30,0.12)', marginTop:'20px', borderRadius:'8px', paddingLeft:'16px', paddingRight:'16px'}}>
                                  <h4 style={{color:'#fca5a5', marginBottom:'10px'}}>☢️ DATABASE RESET</h4>
                                  <Button color="error" variant="outlined" onClick={handleMasterReset}>Master reset</Button>
                              </div>
                          )}
                      </Paper>
                  )}

                  {adminSubTab === 'logs' && (
                      <Paper variant="outlined" sx={{textAlign: 'left', maxWidth: 900, mx: 'auto', p: 3, borderRadius: 2}}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
                            <Typography variant="h6">Activity logs</Typography>
                            <TextField
                              select
                              label="User"
                              value={logSelectedUser}
                              onChange={e=>setLogSelectedUser(e.target.value)}
                              SelectProps={{ native: true }}
                              size="small"
                              sx={{ width: { xs: '100%', sm: 260 } }}
                            >
                              <option value="">All users</option>
                              {usersList.map(u => <option key={u.initials} value={u.initials}>{u.displayName}</option>)}
                            </TextField>
                          </Stack>
                          <Paper variant="outlined" sx={{ maxHeight: 520, overflowY: 'auto', borderRadius: 2 }}>
                              {auditLogs.filter(l => (!logSelectedUser || l.actorInitials === logSelectedUser) && (l.action.includes("Availability"))).map(l => (
                                  <Box key={l.id} sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                                      <Typography variant="caption" sx={{ opacity: 0.7, fontWeight: 800, textTransform:'uppercase' }}>
                                        {l.timestamp?.toDate().toLocaleString()}
                                      </Typography>
                                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, mb: 0.5 }}>
                                        <Chip size="small" variant="outlined" label={l.actor} />
                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{l.action}</Typography>
                                      </Stack>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: (l.details || '').includes('Unavailable') ? 'error.main' : 'text.primary',
                                          fontWeight: (l.details || '').includes('Unavailable') ? 800 : 500,
                                        }}
                                      >
                                        {l.details}
                                      </Typography>
                                  </Box>
                              ))}
                          </Paper>
                      </Paper>
                  )}
              </Box>
          )}
      </Container>

      {/* WEEKEND AVAILABILITY DIALOG */}
      <Dialog open={isPopupOpen && !!selectedDate} onClose={() => setIsPopupOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ pr: 6 }}>
          {selectedDate ? `${selectedDate.day} ${monthNames[selectedDate.month]} ${selectedDate.year}` : 'Weekend'}
          <IconButton
            onClick={() => setIsPopupOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            aria-label="Close"
          >
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {selectedDate?.data?.managerDecision && (
            <Paper variant="outlined" sx={{ p: 1.25, mb: 2, borderRadius: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Typography variant="body2" sx={{ fontWeight: 800 }}>Manager decision</Typography>
                <Chip
                  label={String(selectedDate.data.managerDecision).toUpperCase()}
                  color={selectedDate.data.managerDecision === 'Working' ? 'success' : 'error'}
                />
              </Stack>
            </Paper>
          )}

          <Typography variant="overline" sx={{ opacity: 0.75 }}>Availability</Typography>
          <Stack spacing={1.25} sx={{ mt: 1.25, mb: 2 }}>
            <Button
              variant={selectedDate?.data?.status === 'Want to Work' ? 'contained' : 'outlined'}
              onClick={() => handlePopupSave('Want to Work')}
            >
              Want to work
            </Button>
            <Button
              color="success"
              variant={selectedDate?.data?.status === 'Available' ? 'contained' : 'outlined'}
              onClick={() => handlePopupSave('Available')}
            >
              Available
            </Button>
            <Button
              color="inherit"
              variant={selectedDate?.data?.status === 'Unavailable' ? 'contained' : 'outlined'}
              onClick={() => handlePopupSave('Unavailable')}
            >
              Unavailable
            </Button>
          </Stack>

          <TextField
            label="Notes"
            placeholder="e.g. can only work AM"
            value={selectedDate?.data?.timeNote || ''}
            onChange={handlePopupNoteChange}
            multiline
            minRows={3}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1, justifyContent: 'space-between' }}>
          <Button variant="outlined" onClick={() => setIsPopupOpen(false)}>Done</Button>
          <Button variant="contained" color="success" onClick={handlePopupSaveAndClose}>
            Save changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* PAYROLL QUERY DIALOG */}
      <Dialog open={isQueryModalOpen} onClose={() => setIsQueryModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pr: 6 }}>
          Ask a manager a question
          <IconButton
            onClick={() => setIsQueryModalOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            aria-label="Close"
          >
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Question"
            value={queryText}
            onChange={e => setQueryText(e.target.value)}
            placeholder="e.g. Can you confirm the date? Is there documentation?"
            multiline
            minRows={4}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button variant="outlined" onClick={() => setIsQueryModalOpen(false)}>Cancel</Button>
          <Button onClick={handleAddQuery}>Send</Button>
        </DialogActions>
      </Dialog>

      {/* MANAGER ANSWER DIALOG */}
      <Dialog open={isAnswerModalOpen} onClose={() => setIsAnswerModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pr: 6 }}>
          Respond to query
          <IconButton
            onClick={() => setIsAnswerModalOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            aria-label="Close"
          >
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 2 }}>
          {answerRequestId && (() => {
            const req = allOtRequests.find(r => r.id === answerRequestId);
            const q = req && req.queries ? req.queries.find(x => x.id === answerQueryId) : null;
            if (!q) return <Typography variant="body2">Query not found.</Typography>;
            return (
              <>
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    Question from {q.askedByName}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.5 }}>
                    “{q.question}”
                  </Typography>
                </Paper>

                <RadioGroup value={answerChoice} onChange={e => setAnswerChoice(e.target.value)} row>
                  <FormControlLabel value="approve" control={<Radio />} label="Answer" />
                  <FormControlLabel value="reject" control={<Radio />} label="Reject query" />
                </RadioGroup>

                <TextField
                  label={answerChoice === 'approve' ? 'Your answer' : 'Reason for rejection'}
                  value={answerText}
                  onChange={e => setAnswerText(e.target.value)}
                  placeholder={answerChoice === 'approve' ? 'Provide your answer...' : 'Explain why you cannot answer this query...'}
                  multiline
                  minRows={4}
                />
              </>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button variant="outlined" onClick={() => setIsAnswerModalOpen(false)}>Cancel</Button>
          <Button color="success" onClick={handleSubmitAnswer}>Submit</Button>
        </DialogActions>
      </Dialog>

      {/* DENY REASON DIALOG */}
      <Dialog open={isDenyPopupOpen} onClose={() => setIsDenyPopupOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ pr: 6 }}>
          Deny request
          <IconButton
            onClick={() => setIsDenyPopupOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            aria-label="Close"
          >
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Reason"
            value={denyReason}
            onChange={e=>setDenyReason(e.target.value)}
            placeholder="e.g. Already covered..."
            multiline
            minRows={3}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button variant="outlined" onClick={() => setIsDenyPopupOpen(false)}>Cancel</Button>
          <Button color="error" onClick={handleDenySubmit}>Confirm</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default App;